import { useEffect, useId, useRef, useState } from 'react'
import { receiveSend } from '@features/send/api/sendApi'
import { decodeLinkKey, decryptSendPayload } from '@features/send/services/sendCipher'
import {
  decryptSendKeyWithPassword,
  downloadAndDecryptSendFile,
} from '@features/send/services/sendFlow'
import { type SendPayload, type ReceiveSendResponse } from '@features/send/types'
import { CLIPBOARD_DEFAULT_TTL_SEC, copyToClipboard } from '@services/clipboard'
import { LANDING_ROUTE } from '@constants/routes'
import { Notice } from '@shared/ui/Notice'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * Send 公開受信ページ (T-D-Send / Phase 4 パスワード対応)。
 *
 * 認証不要 — MainLayout / AuthLayout をラップしない。
 * ルート: #send/view/:id/:encodedKey  (パスワードなし)
 *         #send/view/:id              (パスワードあり)
 *
 * パスワードなし:
 *   1. URL :encodedKey → decodeLinkKey
 *   2. GET /api/send/receive/:id → cipher 取得 (accessCount +1)
 *   3. linkKey で復号 → 表示
 *
 * パスワードあり (encodedKey = ''):
 *   1. GET /api/send/receive/:id → { hasPassword:true, passSalt, protectedSendKey, pskIv }
 *   2. パスワード入力 UI を表示
 *   3. PBKDF2(password, passSalt) → passKey → AES-GCM.open(protectedSendKey) → linkKey
 *   4. linkKey で復号 → 表示
 */

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`

  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

type ReceiveState =
  | { kind: 'loading' }
  | { kind: 'needs_password'; resp: ReceiveSendResponse }
  | { kind: 'verifying_password' }
  | { kind: 'ready'; payload: SendPayload; linkKey: Uint8Array }
  | { kind: 'error'; messageKey: string }

type DownloadState =
  | { kind: 'idle' }
  | { kind: 'downloading' }
  | { kind: 'done' }
  | { kind: 'failed'; messageKey: string }

interface SendReceivePageProps {
  id: string
  encodedKey: string
}

export const SendReceivePage = ({ id, encodedKey }: SendReceivePageProps): JSX.Element => {
  const { t } = useTranslation()
  const passwordId = useId()
  const [receiveState, setReceiveState] = useState<ReceiveState>({ kind: 'loading' })
  const [copied, setCopied] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [downloadState, setDownloadState] = useState<DownloadState>({ kind: 'idle' })
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    const run = async (): Promise<void> => {
      if (!id) {
        if (mountedRef.current) {
          setReceiveState({ kind: 'error', messageKey: 'send.receive.error_not_found' })
        }

        return
      }

      try {
        const resp = await receiveSend(id)

        if (!mountedRef.current) return

        // パスワード保護あり → パスワード入力 state へ
        if (resp.hasPassword) {
          setReceiveState({ kind: 'needs_password', resp })

          return
        }

        // パスワードなし → encodedKey から linkKey を取得して復号
        if (!encodedKey) {
          setReceiveState({ kind: 'error', messageKey: 'send.receive.error_decrypt' })

          return
        }

        let linkKey: Uint8Array
        try {
          linkKey = decodeLinkKey(encodedKey)
        } catch {
          setReceiveState({ kind: 'error', messageKey: 'send.receive.error_decrypt' })

          return
        }

        const payload = await decryptSendPayload(linkKey, resp.cipher)

        if (mountedRef.current) setReceiveState({ kind: 'ready', payload, linkKey })
      } catch (err) {
        if (!mountedRef.current) return
        if (err instanceof Error) {
          if (err.message === 'send_gone') {
            setReceiveState({ kind: 'error', messageKey: 'send.receive.error_gone' })
          } else if (err.message === 'send_not_found') {
            setReceiveState({ kind: 'error', messageKey: 'send.receive.error_not_found' })
          } else {
            setReceiveState({ kind: 'error', messageKey: 'send.receive.error_generic' })
          }
        } else {
          setReceiveState({ kind: 'error', messageKey: 'send.receive.error_generic' })
        }
      }
    }

    void run()

    return () => {
      mountedRef.current = false
    }
  }, [id, encodedKey])

  const onPasswordSubmit = async (): Promise<void> => {
    if (receiveState.kind !== 'needs_password') return
    if (!passwordInput) return

    const { resp } = receiveState

    if (!resp.passSalt || !resp.protectedSendKey || !resp.pskIv) {
      setPasswordError(t('send.receive.error_decrypt'))

      return
    }

    setReceiveState({ kind: 'verifying_password' })
    setPasswordError(null)

    try {
      const linkKey = await decryptSendKeyWithPassword(
        passwordInput,
        resp.passSalt,
        resp.protectedSendKey,
        resp.pskIv,
      )

      const payload = await decryptSendPayload(linkKey, resp.cipher)

      if (mountedRef.current) setReceiveState({ kind: 'ready', payload, linkKey })
    } catch {
      if (!mountedRef.current) return
      // GCM auth tag 不一致 → パスワード間違い
      setReceiveState({ kind: 'needs_password', resp })
      setPasswordError(t('send.receive.error_wrong_password'))
    }
  }

  const onDownloadFile = async (): Promise<void> => {
    if (receiveState.kind !== 'ready') return
    if (receiveState.payload.type !== 'file') return

    setDownloadState({ kind: 'downloading' })

    try {
      const plaintext = await downloadAndDecryptSendFile(id, receiveState.linkKey)
      const mime = receiveState.payload.mimeType ?? 'application/octet-stream'
      // Uint8Array → Blob → ObjectURL → <a download> click → revoke
      const blobInit: BlobPart[] = [plaintext as Uint8Array<ArrayBuffer>]
      const blob = new Blob(blobInit, { type: mime })
      const url = URL.createObjectURL(blob)
      try {
        const a = document.createElement('a')

        a.href = url
        a.download = receiveState.payload.name || 'ookey-send'
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        a.remove()
      } finally {
        URL.revokeObjectURL(url)
      }
      if (mountedRef.current) setDownloadState({ kind: 'done' })
    } catch (err) {
      if (!mountedRef.current) return
      if (err instanceof Error && err.message === 'send_gone') {
        setDownloadState({ kind: 'failed', messageKey: 'send.receive.error_gone' })
      } else {
        setDownloadState({ kind: 'failed', messageKey: 'send.receive.error_decrypt' })
      }
    }
  }

  const onCopy = async (): Promise<void> => {
    if (receiveState.kind !== 'ready') return
    if (receiveState.payload.type !== 'text' || !receiveState.payload.text) return

    try {
      await copyToClipboard(receiveState.payload.text)
      setCopied(true)
      setTimeout(() => {
        if (mountedRef.current) setCopied(false)
      }, CLIPBOARD_DEFAULT_TTL_SEC * 1000)
    } catch {
      // copy 失敗は silent
    }
  }

  return (
    <div className="send-receive-root">
      <div className="send-receive-container">
        <div className="send-receive-logo">
          <Icon name="send" aria-hidden="true" />
          <span>OoKey Send</span>
        </div>

        {receiveState.kind === 'loading' && (
          <div className="send-receive-loading">
            <div className="send-receive-spinner" aria-hidden="true" />
            <p>{t('send.receive.loading')}</p>
          </div>
        )}

        {receiveState.kind === 'error' && (
          <div className="send-receive-error">
            <Notice tone="danger" ico="alert">
              {t(receiveState.messageKey)}
            </Notice>
          </div>
        )}

        {(receiveState.kind === 'needs_password' || receiveState.kind === 'verifying_password') && (
          <div className="card send-receive-card">
            <div className="send-receive-title">
              <h1 className="send-receive-h1">{t('send.receive.password_title')}</h1>
              <p className="send-receive-sub">{t('send.receive.password_subtitle')}</p>
            </div>

            {receiveState.kind === 'needs_password' && receiveState.resp.passwordHint && (
              <p className="send-receive-hint">
                {t('send.receive.password_hint_label')}: {receiveState.resp.passwordHint}
              </p>
            )}

            <div className="field">
              <label className="field-label" htmlFor={passwordId}>
                {t('send.receive.password_label')}
              </label>
              <PasswordInput
                id={passwordId}
                value={passwordInput}
                onChange={ev => setPasswordInput(ev.target.value)}
                onKeyDown={ev => {
                  if (ev.key === 'Enter') void onPasswordSubmit()
                }}
                disabled={receiveState.kind === 'verifying_password'}
                visible={showPw}
                onVisibleChange={setShowPw}
                autoFocus
              />
              {/* パスワード誤り時はエラーを input のすぐ下に赤字で出す。 */}
              {passwordError !== null && (
                <p className="form-error send-receive-pw-error" role="alert">
                  {passwordError}
                </p>
              )}
            </div>

            <button
              type="button"
              className="btn btn-primary btn-block"
              disabled={receiveState.kind === 'verifying_password' || !passwordInput}
              onClick={() => void onPasswordSubmit()}
            >
              {receiveState.kind === 'verifying_password'
                ? t('send.receive.password_verifying')
                : t('send.receive.password_submit')}
            </button>
          </div>
        )}

        {receiveState.kind === 'ready' && (
          <div className="card send-receive-card">
            <div className="send-receive-title">
              <h1 className="send-receive-h1">{t('send.receive.title')}</h1>
              <p className="send-receive-sub">{t('send.receive.subtitle')}</p>
            </div>

            <div className="send-receive-field">
              <div className="field-label">{t('send.receive.name_label')}</div>
              <div className="send-receive-name nm">{receiveState.payload.name}</div>
            </div>

            {receiveState.payload.type === 'text' && receiveState.payload.text && (
              <div className="send-receive-field">
                <div className="field-label">{t('send.receive.content_label')}</div>
                <div className="send-receive-content">
                  <pre className="send-receive-pre">{receiveState.payload.text}</pre>
                  <button
                    type="button"
                    className={`btn send-receive-copy-btn${copied ? ' btn-secondary' : ' btn-primary'}`}
                    onClick={() => void onCopy()}
                  >
                    <Icon name={copied ? 'check' : 'copy'} aria-hidden="true" />
                    {t('send.receive.copy_content')}
                  </button>
                </div>
              </div>
            )}

            {receiveState.payload.type === 'file' && (
              <div className="send-receive-field" data-testid="send-receive-file">
                <div className="field-label">{t('send.receive.file_label')}</div>
                <div className="send-receive-file-meta">
                  <Icon name="file-text" aria-hidden="true" />
                  <span className="send-receive-file-name">{receiveState.payload.name}</span>
                  {receiveState.payload.size !== undefined && (
                    <span className="send-receive-file-size">
                      ({formatBytes(receiveState.payload.size)})
                    </span>
                  )}
                </div>
                {downloadState.kind === 'failed' && (
                  <p className="form-error" role="alert">
                    {t(downloadState.messageKey)}
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-primary send-receive-download-btn"
                  onClick={() => void onDownloadFile()}
                  disabled={downloadState.kind === 'downloading'}
                >
                  <Icon name="download" aria-hidden="true" />
                  {downloadState.kind === 'downloading'
                    ? t('send.receive.downloading')
                    : downloadState.kind === 'done'
                      ? t('send.receive.downloaded')
                      : t('send.receive.download')}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="send-receive-footer">
          <a
            href={`#${LANDING_ROUTE}`}
            className="send-receive-home-link"
            data-testid="send-receive-home"
          >
            <Icon name="home" aria-hidden="true" />
            {t('send.receive.home_link')}
          </a>
        </div>
      </div>
    </div>
  )
}
